import { Component, OnInit, OnDestroy, Inject, AfterViewInit } from '@angular/core';
import { catchError, debounceTime, first, forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { DataInput, DataInputOption, InfoDialogComponent, RequestInProgressService }  from '@trivelope/triv-ui-tools-lib';
import { Environment } from 'src/app/classes/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Editor, toHTML, Toolbar } from 'ngx-editor';
import { News, Rubric } from 'src/app/classes/news';
import { NewsService } from 'src/app/services/news.service';
import { MyFile } from 'src/app/classes/fileInfo';
import { MatDialog } from '@angular/material/dialog';
import { CalAndNewsFileService } from 'src/app/services/file.calendarAndNews.service';
import { CategoryService } from 'src/app/services/news-category.service';
import { FileService } from 'src/app/services/file.service';


@Component({
	selector: 'app-news-editor',
	templateUrl: './news-editor.component.html',
	styleUrls: [ './news-editor.component.css' ],
})
export class NewsEditorComponent implements OnInit, AfterViewInit, OnDestroy {


	bInProgress = true;
	subscriptions = new Subscription();
	myInputs: { [name: string]: DataInput } = {};
	formG1 = new FormGroup({});
	rubrics: Rubric[] = [];
	news = new News();

	editor = new Editor();
	toolbar: Toolbar = [
		[ 'bold', 'italic', 'underline', 'strike' ],
		// ['code', 'blockquote'],
		['ordered_list', 'bullet_list'],
		[ { heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] } ],
		['link', 'image'],
		['text_color', 'background_color'],
		['align_left', 'align_center', 'align_right', 'align_justify'],
	];

	bExternal = false;

	attachedFiles: MyFile[] = [];
	readonly FILESIZE_MAX_MB_TOTAL = 100;

	tmsLastSaveStart: number | null = null;
	tmsLastSaveEnd: number | null = null;


	constructor(
		@Inject('environment') private environment: Environment,
		private activatedRoute: ActivatedRoute,
		private newsService: NewsService,
		private matSnackBar: MatSnackBar,
		private router: Router,
		private matDialog: MatDialog,
		private calAndNewsFileService: CalAndNewsFileService,
		private categoryService: CategoryService,
		private fileService: FileService
	) {
		this.formG1.addControl( "news_content", new FormControl() );
	}


	ngOnInit() {

		for( let dataInput of this.environment.news ) {
			this.myInputs[ dataInput.id ] = dataInput;
		}

		const categories = this.categoryService.getCategoriesInCache()
			.sort( (a,b) => a.bezeichnung.localeCompare( b.bezeichnung ) );
		const categoryOptions: DataInputOption[] = [];
		if( categories.length ) {
			for( const category of categories ) {
				categoryOptions.push( { id: "" + category.kategorieId, label: category.bezeichnung } );
			}
		}
		else {
			this.myInputs[ "news_categories" ].multiple = false;
			categoryOptions.push( { id: "", label: "Zurzeit sind keine Kategorien verfügbar" } );
			this.formG1.patchValue( { news_categories: [ categoryOptions[0] ] } );
		}
		this.myInputs[ "news_categories" ].options = categoryOptions;


		forkJoin( {
			rubrics: this.newsService.getRubrics(),
			routeParams: this.activatedRoute.queryParams.pipe( first() )		// get only the first, otherwise the obs never stops and forkJoin hangs
		} ).pipe(
			catchError( err => of(null) )
		).subscribe( response => {

			if( !response ) {
				this.bInProgress = false;
				return;
			}

			this.rubrics = response.rubrics.sort( (a,b) => a.name.localeCompare( b.name ) );
			const options: DataInputOption[] = [];
			for( const rubric of this.rubrics ) {
				options.push( { id: "" + rubric.id, label: rubric.name } );
			}
			this.myInputs[ "news_rubrics" ].options = options;


			if( response.routeParams['id'] ) {

				this.newsService.getNews().subscribe( news => {

					this.bInProgress = false;
					if( !news || !news.length ) return;

					const existingNews = news.find( n => n.id == response.routeParams['id'] );
					if( !existingNews ) return;

					this.formG1.enable();

					const diSelectedOptionsRubrics: DataInputOption[] = [];
					for( const i of existingNews.rubrics ) {
						const diOption = this.myInputs[ "news_rubrics" ].options?.find( o => o.id == ("" + i) );
						if( diOption ) {
							diSelectedOptionsRubrics.push( diOption );
						}
					}

					const diSelectedOptionsCategories: DataInputOption[] = [];
					for( const i of existingNews.categories ) {
						const diOption = this.myInputs[ "news_categories" ].options?.find( o => o.id == ("" + i) );
						if( diOption ) {
							diSelectedOptionsCategories.push( diOption );
						}
					}


					// setTimeout to avoid NG0100
					setTimeout( () => {

						this.news = existingNews;

						this.formG1.patchValue( {
							news_title: existingNews.title,
							news_rubrics: diSelectedOptionsRubrics,
							news_categories: diSelectedOptionsCategories,
							news_contactTitle: existingNews.contactTitle,
							news_contactFamilyName: existingNews.contactFamilyName,
							news_contactFirstName: existingNews.contactFirstName,
							news_contactPhone: existingNews.contactPhone,
							news_contactEmail: existingNews.contactEmail,
							news_external: !!existingNews.url,
							news_url: existingNews.url,
							news_content: existingNews.content
						});

						this.attachedFiles = existingNews.myFiles.sort( (a,b) => a.file.name.localeCompare( b.file.name ) );

						this.listenToChanges();

					}, 1 );

				});

			}
			else {
				this.formG1.enable();
				this.listenToChanges();
				this.bInProgress = false;
			}


		});




	}

	ngAfterViewInit() {

		this.subscriptions.add(
			this.formG1.get( "news_external" )?.valueChanges.subscribe( b => {
				this.bExternal = b;
			})
		);

		this.formG1.disable();

	}

	ngOnDestroy() {

		this.editor.destroy();
		this.subscriptions.unsubscribe();

	}


	private listenToChanges() {

		this.subscriptions.add(
			this.formG1.valueChanges.pipe(
				debounceTime( 400 ),
				switchMap( values => {
					console.log( "VALUES CHANGED", values );
					return this.save( this.getNewsFromForm() );
				})
			).subscribe()
		);

	}

	clickPublish() {

		if( !this.formG1.valid ) return;
		const previousReleaseState = this.news.bReleased;

		this.bInProgress = true;
		this.newsService.publishNews( this.news.id ).subscribe( b => {

			this.bInProgress = false;
			if( b ) {

				this.matSnackBar.open( "Die Nachricht wurde freigegeben", "Freigeben" );

				if( !previousReleaseState ) this.matSnackBar.open( "Die Nachricht wurde freigegeben", "Freigeben" );
				else this.matSnackBar.open( "Die Änderungen wurden gesendet", "Freigeben" );

				this.news.bReleased = true;

			}
			else {
				this.matSnackBar.open( "Beim Freigeben der Nachrichten ist ein Fehler aufgetreten", "Fehler" );
			}
		});

	}

	private save( news: News ): Observable<string> {

		this.tmsLastSaveStart = ( new Date() ).getTime();
		this.bInProgress = true;

		return this.newsService.saveNews( news ).pipe(

			tap( newsId => {

				this.tmsLastSaveEnd = ( new Date() ).getTime();
				this.bInProgress = false;

				if( newsId ) {

					this.news.id = newsId;
					this.matSnackBar.open( "Die Nachricht wurde gespeichert", "Speichern" );

				}

			} )

		);

	}

	private getNewsFromForm(): News {

		const news = { ...new News(), ...this.news } as News;

		news.title = this.formG1.get( "news_title" )?.value || "";
		news.rubrics = [];
		const diOptions: DataInputOption[] = this.formG1.get( "news_rubrics" )?.value || [];
		if( diOptions && diOptions.length ) {
			for( const o of diOptions ) news.rubrics.push( o.id );
		}
		news.categories = [];
		const diOptionsCateg: DataInputOption[] = this.formG1.get( "news_categories" )?.value || [];
		if( diOptionsCateg && diOptionsCateg.length ) {
			for( const o of diOptionsCateg ) {
				if( o.id ) news.categories.push( o.id );
			}
		}
		news.contactTitle = this.formG1.get( "news_contactTitle" )?.value || "";
		news.contactFamilyName = this.formG1.get( "news_contactFamilyName" )?.value || "";
		news.contactFirstName = this.formG1.get( "news_contactFirstName" )?.value || "";
		news.contactPhone = this.formG1.get( "news_contactPhone" )?.value || "";
		news.contactEmail = this.formG1.get( "news_contactEmail" )?.value || "";

		if( !this.bExternal ) {
			this.formG1.patchValue( { news_url: "" }, { emitEvent: false } );
			news.url = "";
		}
		else {
			news.url = this.formG1.get( "news_url" )?.value || "";
		}

		news.content = "";
		const ctrlContent = this.formG1.get( "news_content" );
		if( ctrlContent && ctrlContent.value ) {
			// console.warn( "TYP", ctrlDesc.value, typeof( ctrlDesc.value ) )
			if( ctrlContent.value[ 'type' ] && ctrlContent.value[ 'type' ] == "doc" ) {
				news.content = toHTML( ctrlContent.value );
				// console.warn( "toHTML", toHTML( ctrlDesc.value ) )
			}
			else {
				news.content = ctrlContent.value;
			}
		}

		return news;

	}


	clickDelete() {

		this.bInProgress = true;
		this.newsService.deleteNews( this.news ).subscribe( b => {

			this.bInProgress = false;
			if( b ) {
				this.router.navigate( [ "News" ] );
				this.matSnackBar.open( "Die Nachricht wurde gelöscht", "Löschen" );
			}
			else {
				this.matSnackBar.open( "Beim Löschen der Nachrichten ist ein Fehler aufgetreten", "Fehler" );
			}

		});

	}

	onDescriptionChange( html: string ) {

		if( html != this.news.content ) {
			this.formG1.markAsTouched();
		}

	}





	aFileWasChosen( file: File ) {

		if( !file ) return;
		this.checkAndUpload( [ file ] );

	}

	filesWereChosen( files: File[] ) {

		if( !files ) return;
		this.checkAndUpload( files );

	}

	private checkAndUpload( newFiles: File[] ) {

		let totalSize = 0;
		for( const attachedFile of this.attachedFiles ) {

			totalSize += attachedFile.file.size;

			for( const newFile of newFiles ) {
				if(  ( attachedFile.file.name == newFile.name ) && ( attachedFile.file.size == newFile.size )  ) {
					this.matDialog.open( InfoDialogComponent, {
						data: { title: "Duplikat", message: "Diese Datei ist bereits enthalten." }
					});
					return;
				}
			}

		}

		for( const newFile of newFiles ) totalSize += newFile.size;

		if(  totalSize >= (this.FILESIZE_MAX_MB_TOTAL * 1024 * 1024)  ) {
			this.matDialog.open( InfoDialogComponent, {
				data: { title: "", message: "Die Gesamtdateigröße ist auf " + this.FILESIZE_MAX_MB_TOTAL + " MB begrenzt" }
			});
			return;
		}

		const filesToUpload: MyFile[] = [];
		for( const newFile of newFiles ) {

			const myFile = new MyFile();
			myFile.file = newFile;

			filesToUpload.push( myFile );
			this.attachedFiles.push( myFile );
		}

		this.bInProgress = true;
		this.calAndNewsFileService.uploadNewsFiles( this.news.id, filesToUpload ).subscribe( b => {
			this.bInProgress = false;
		});

	}

	deleteFile( myFile: MyFile ) {

		if( !myFile.id ) {

			this.matDialog.open( InfoDialogComponent, {
				data: { title: "", message: "Die Datei wird noch hochgeladen" }
			});
			return;

		}
		else {

			this.bInProgress = true;
			this.calAndNewsFileService.deleteUploadedFile( myFile.id ).subscribe( b => {
				this.bInProgress = false;
				if( b ) {
					const nPos = this.attachedFiles.findIndex( f => f.id == myFile.id );
					if( nPos != -1 ) this.attachedFiles.splice( nPos, 1 );
				}
				else {
					this.matDialog.open( InfoDialogComponent, {
						data: { title: "Fehler", message: "die Datei wurde nicht gelöscht" }
					});
				}
			});

		}

	}

	downloadFile( attachedFile: MyFile ) {

		if( attachedFile.percentageUploaded != 100 ) {
			this.matDialog.open( InfoDialogComponent, {
				data: { title: "", message: "Die Datei wird noch hochgeladen" }
			});
			return;
		}

		this.fileService.downloadFileById( attachedFile.id, attachedFile.file.name );

	}


}
